"use client";

import React from "react";
import {
  ArrowClockwiseIcon,
  ArrowRightIcon,
  CoatHangerIcon,
  CreditCardIcon,
  DressIcon,
  ImageSquareIcon,
  MagicWandIcon,
  MapPinIcon,
  PaletteIcon,
  PencilLineIcon,
  RulerIcon,
  SealCheckIcon,
  ShirtFoldedIcon,
  SparkleIcon,
  SwatchesIcon,
  TagIcon,
  TShirtIcon,
} from "@phosphor-icons/react";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import {
  getDesignerRenderActionLabel,
  isDesignerRenderCommitPreset,
} from "@/lib/agent/designerActionPresentation";
import { AgentWidget, PresetOption } from "@/types/createAgent";
import {
  ActionOptionVisual,
  hasActionOptionVisual,
} from "../visuals/ActionOptionVisuals";

export type WidgetTone =
  | "credit"
  | "success"
  | "primary"
  | "neutral"
  | "choice";

export interface PresetWidgetMeta {
  icon: React.ReactNode;
  kicker: string;
  badge?: string;
  tone: WidgetTone;
  isCommitAction: boolean;
}

export function getWidgetConceptLayout(widget: AgentWidget): string {
  return normalizeWidgetType(widget.type).replace(/_/g, "-");
}

/** Customer-facing label for a preset button; generation/modify actions get
 * stable verbs instead of whatever label the planner produced. */
export function getPresetDisplayLabel(preset: PresetOption): string {
  if (isDesignerRenderCommitPreset(preset)) {
    return getDesignerRenderActionLabel(preset.actionId) ?? preset.label;
  }
  return preset.label;
}

export function getPresetChoiceVisual(
  preset: PresetOption
): React.ReactNode | null {
  return hasActionOptionVisual(preset) ? (
    <ActionOptionVisual preset={preset} />
  ) : null;
}

export function getPresetKicker(preset: PresetOption): string {
  switch (preset.actionId) {
    case "generate_dress_front":
      return "Front image";
    case "generate_dress_back":
      return "Back image";
    case "modify_dress_front":
      return "Edit front image";
    case "modify_dress_back":
      return "Edit back image";
    case "buy_credits_back":
    case "buy_credits_modify":
      return "Add credits";
    case "submit_quote":
      return "Submit quote";
    case "start_virtual_try_on":
      return "Your photo";
    case "continue_to_back_design":
      return "Next step";
    case "request_location":
      return "Add location";
    case "start_over":
      return "Restart";
    case "set_preset_type":
      return "Occasion";
    case "set_color":
      return "Color";
    case "set_length":
      return "Length";
    case "set_sleeve":
      return "Sleeve";
    case "set_neckline":
      return "Neckline";
    case "set_silhouette":
      return "Silhouette";
    case "set_fabric":
      return "Fabric";
    case "set_back_prompt":
    case "set_back_style":
      return "Back view";
    case "modify_field":
      return "Refinement";
    case "suggest_field":
      if (preset.actionData?.suggestedField === "color") {
        return "Color";
      }
      if (
        preset.actionData?.suggestedField === "upload" ||
        preset.actionData?.suggestedField === "image_upload" ||
        preset.actionData?.suggestedField === "front_upload" ||
        preset.actionData?.suggestedField === "back_upload"
      ) {
        return "Reference image";
      }
      return "Detail";
    case "custom":
    default:
      return "Option";
  }
}

export function getPresetMeta(preset: PresetOption): PresetWidgetMeta {
  switch (preset.actionId) {
    case "generate_dress_front":
    case "generate_dress_back":
      return {
        icon: <ImageSquareIcon size={18} weight="duotone" aria-hidden="true" />,
        badge: "1 credit",
        kicker: getPresetKicker(preset),
        tone: "credit",
        isCommitAction: true,
      };
    case "modify_dress_front":
    case "modify_dress_back":
      return {
        icon: <MagicWandIcon size={18} weight="duotone" aria-hidden="true" />,
        badge: "1 credit",
        kicker: getPresetKicker(preset),
        tone: "credit",
        isCommitAction: true,
      };
    case "buy_credits_back":
    case "buy_credits_modify":
      return {
        icon: <CreditCardIcon size={18} weight="duotone" aria-hidden="true" />,
        badge: "Checkout",
        kicker: getPresetKicker(preset),
        tone: "primary",
        isCommitAction: true,
      };
    case "submit_quote":
      return {
        icon: <SealCheckIcon size={18} weight="duotone" aria-hidden="true" />,
        badge: "Free",
        kicker: getPresetKicker(preset),
        tone: "success",
        isCommitAction: true,
      };
    case "start_virtual_try_on":
      return {
        icon: <SparkleIcon size={18} weight="duotone" aria-hidden="true" />,
        badge: "Free",
        kicker: getPresetKicker(preset),
        tone: "success",
        isCommitAction: true,
      };
    case "continue_to_back_design":
      return {
        icon: <ArrowRightIcon size={18} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "neutral",
        isCommitAction: true,
      };
    case "request_location":
      return {
        icon: <MapPinIcon size={18} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "neutral",
        isCommitAction: true,
      };
    case "start_over":
      return {
        icon: (
          <ArrowClockwiseIcon size={18} weight="duotone" aria-hidden="true" />
        ),
        kicker: getPresetKicker(preset),
        tone: "neutral",
        isCommitAction: true,
      };
    case "set_preset_type":
      return {
        icon: <TagIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_color":
      return {
        icon: <PaletteIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_length":
      return {
        icon: <RulerIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_sleeve":
      return {
        icon: <ShirtFoldedIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_neckline":
      return {
        icon: <TShirtIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_silhouette":
      return {
        icon: <DressIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_fabric":
      return {
        icon: <SwatchesIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "set_back_prompt":
    case "set_back_style":
      return {
        icon: <CoatHangerIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "modify_field":
      return {
        icon: <PencilLineIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "suggest_field":
      return {
        icon:
          preset.actionData?.suggestedField === "upload" ||
          preset.actionData?.suggestedField === "image_upload" ||
          preset.actionData?.suggestedField === "front_upload" ||
          preset.actionData?.suggestedField === "back_upload" ? (
            <ImageSquareIcon size={18} weight="duotone" aria-hidden="true" />
          ) : (
            <SparkleIcon size={16} weight="duotone" aria-hidden="true" />
          ),
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    case "custom":
      return {
        icon: <DressIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
    default:
      return {
        icon: <SparkleIcon size={16} weight="duotone" aria-hidden="true" />,
        kicker: getPresetKicker(preset),
        tone: "choice",
        isCommitAction: false,
      };
  }
}

export function getIconClass(tone: WidgetTone): string {
  switch (tone) {
    case "credit":
      return "bg-brand-logo text-brand-logo-foreground shadow-sm shadow-brand-logo/20";
    case "success":
      return "bg-success/12 text-success ring-1 ring-success/20";
    case "primary":
      return "bg-info/12 text-info ring-1 ring-info/20";
    case "neutral":
      return "bg-muted text-foreground ring-1 ring-border";
    case "choice":
    default:
      return "bg-background text-foreground ring-1 ring-border";
  }
}

export function getBadgeClass(tone: WidgetTone): string {
  switch (tone) {
    case "credit":
      return "bg-warning text-warning-foreground ring-warning/40";
    case "success":
      return "bg-success/12 text-success ring-success/20";
    case "primary":
      return "bg-info/12 text-info ring-info/20";
    case "neutral":
    case "choice":
    default:
      return "bg-muted text-foreground/75 ring-border";
  }
}
